import { callFunction } from '../firebase/functions';

// Public half of the VAPID keypair — MUST match VAPID_PUBLIC_KEY in
// functions/notifications.js (see that file's comment on rotation).
const VAPID_PUBLIC_KEY = 'BJ8iUTwRUnDB0rRYJiZSmc3tsp06rm_qyTO62_ykNLZJkBIfznffd6cdAdKnAeuqN0FgcuXUtcYSJ17CisF64po';

function urlBase64ToUint8Array(base64) {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(padded);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent || '')
    // iPadOS reports itself as a Mac — touch support gives it away.
    || (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
}

export function isStandalone() {
  try {
    return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  } catch {
    return false;
  }
}

// 'supported' | 'needsInstall' (iOS Safari tab — Web Push only exists once
// the app is added to the home screen) | 'unsupported'.
export function pushSupport() {
  if (typeof window === 'undefined') return 'unsupported';
  if (isIOS() && !isStandalone()) return 'needsInstall';
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return 'unsupported';
  return 'supported';
}

async function getRegistration() {
  // PwaInstallPrompt normally registers /sw.js on Academy Portal load;
  // registering again here is a no-op if it already did, and covers the
  // case where this runs first.
  await navigator.serviceWorker.register('/sw.js');
  return navigator.serviceWorker.ready;
}

export async function getCurrentSubscription() {
  if (pushSupport() !== 'supported') return null;
  const reg = await getRegistration();
  return reg.pushManager.getSubscription();
}

// Must be called from a user gesture (a button click) — iOS in particular
// refuses the permission prompt otherwise.
export async function enablePush() {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { ok: false, permission };
  const reg = await getRegistration();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
  }
  await callFunction('savePushSubscription')({ subscription: sub.toJSON(), userAgent: navigator.userAgent });
  return { ok: true, permission };
}

export async function disablePush() {
  const sub = await getCurrentSubscription();
  if (!sub) return;
  await callFunction('removePushSubscription')({ endpoint: sub.endpoint }).catch(() => {});
  await sub.unsubscribe().catch(() => {});
}

// Re-sends this device's existing subscription for the signed-in user —
// keeps the server copy fresh if the browser rotated it, and re-links a
// shared device to whoever is signed in now (see savePushSubscription).
export async function syncExistingSubscription(uid) {
  if (!uid || !hasPushOptIn(uid)) return;
  if (pushSupport() !== 'supported' || Notification.permission !== 'granted') return;
  const sub = await getCurrentSubscription();
  if (sub) await callFunction('savePushSubscription')({ subscription: sub.toJSON(), userAgent: navigator.userAgent });
}

// Remembers, per account on this device, that the person turned push on —
// so syncExistingSubscription only ever re-links this device's
// subscription to an account that actually opted in here, never silently
// to whoever else signs in on a shared family device afterwards.
function optInKey(uid) { return `pushOptIn:${uid}`; }
export function markPushOptIn(uid, on) {
  try { if (on) localStorage.setItem(optInKey(uid), '1'); else localStorage.removeItem(optInKey(uid)); } catch { /* ignore */ }
}
export function hasPushOptIn(uid) {
  try { return localStorage.getItem(optInKey(uid)) === '1'; } catch { return false; }
}

// Called on sign-out (see firebase/auth.js): unlinks this device's
// subscription from the account being signed out, server-side only — the
// browser subscription itself is kept so the same person signing back in
// picks notifications straight back up (syncExistingSubscription). Never
// registers a service worker (staff portals never have one) and gives up
// after a few seconds so a slow network can't hold up signing out.
export async function detachPushForSignOut() {
  try {
    if (!('serviceWorker' in navigator)) return;
    const reg = await navigator.serviceWorker.getRegistration('/');
    const sub = reg && await reg.pushManager?.getSubscription();
    if (!sub) return;
    await Promise.race([
      callFunction('removePushSubscription')({ endpoint: sub.endpoint }),
      new Promise((resolve) => setTimeout(resolve, 3000)),
    ]);
  } catch { /* never block sign-out */ }
}
